import React, {useEffect, useState} from 'react';
import {
    Card,
    Box,
    Stack,
    Switch,
    Typography,
    IconButton,
    Chip,
    Divider,
    Tooltip,
    Fade,
    FormControlLabel,
    Grid,
    Paper
} from "@mui/material";
import {formatDate, fromFirebase} from "./Utils";
import ThumbUp from '@mui/icons-material/ThumbUp';
import Flag from '@mui/icons-material/Flag';
import ThumbDown from "@mui/icons-material/ThumbDown";
import {db} from '../firebase';
import {getAuth} from "firebase/auth";
import {collection, doc, getDocs, query, setDoc, where} from 'firebase/firestore';
import {DateTime} from 'luxon';
import util from 'util';

export default function RequestCardV3(props) {
    var imgRef = React.createRef(null);
    // var imgRef = useRef();
    var [loaded, setLoaded] = useState(false);
    var [dimensions, setDimensions] = useState(null);
    var [username, setUsername] = useState(null);
    var [isNSFW, setNSFW] = useState(false);
    var [isMature, setMature] = useState(false);
    var [containsIP, setContainsIP] = useState(false);

    const [notificationSettings, setNotificationSettings] = useState({
        open: false,
        message: ""
    });

    useEffect(() => {
        const lookupUserById = async (userId) => {
            if (userId === 'imported') {
                setUsername('DiffieBot');
                return;
            }

            const q = query(collection(db, 'users'), where('id', '==', userId));

            const snapshot = await getDocs(q);

            if (snapshot.size === 1) {
                const user = snapshot.docs[0].data()
                setUsername(user.profile.username ?? "[Anonymous]");
            }
        }

        lookupUserById(props.request.submittedBy);

        // Initialize NSFW state (defaulting to false if not set)
        setNSFW(props.request.isNSFW || false);
        setMature(props.request.isNSFW || false);
    }, [props.request.isNSFW, props.request.submittedBy]);

    async function approveRequest(request) {
        const currentUser = getAuth().currentUser;

        if (!currentUser) {
            console.error('Cannot approve request: User not authenticated');
            alert('You must be logged in to approve requests');
            return;
        }

        // Update request object
        request['isNSFW'] = isNSFW;
        request['containsIP'] = containsIP ? true : false;
        request['workflow']['imageWidth'] = imgRef.current.naturalWidth;
        request['workflow']['imageHeight'] = imgRef.current.naturalHeight;
        request['reviewedAt'] = DateTime.utc().startOf('second').toISO({suppressMilliseconds: true});
        request['reviewedBy'] = currentUser.uid;
        request['status'] = 'approved';

        // Save to Firestore immediately
        try {
            await setDoc(doc(db, "feed", request.id), request);
            console.log(`Approved request ${request.id}`);
        } catch (error) {
            console.error('Error approving request:', error);
            alert(`Failed to approve request: ${error.message}`);
        }
    }

    async function rejectRequest(request) {
        const currentUser = getAuth().currentUser;

        if (!currentUser) {
            console.error('Cannot reject request: User not authenticated');
            alert('You must be logged in to reject requests');
            return;
        }

        request['workflow']['imageWidth'] = imgRef.current.naturalWidth;
        request['workflow']['imageHeight'] = imgRef.current.naturalHeight;
        request['reviewedAt'] = DateTime.utc().startOf('second').toISO({suppressMilliseconds: true});
        request['reviewedBy'] = currentUser.uid;
        request['status'] = 'rejected';

        // Save to Firestore immediately
        try {
            await setDoc(doc(db, "feed", request.id), request);
            console.log(`Rejected request ${request.id}`);
        } catch (error) {
            console.error('Error rejecting request:', error);
            alert(`Failed to reject request: ${error.message}`);
        }
    }

    async function flagRequest(request) {
        const currentUser = getAuth().currentUser;

        if (!currentUser) {
            console.error('Cannot flag request: User not authenticated');
            alert('You must be logged in to flag requests');
            return;
        }

        request['workflow']['imageWidth'] = imgRef.current.naturalWidth;
        request['workflow']['imageHeight'] = imgRef.current.naturalHeight;
        request['reviewedAt'] = DateTime.utc().startOf('second').toISO({suppressMilliseconds: true});
        request['reviewedBy'] = currentUser.uid;
        request['status'] = 'flagged';

        // Save to Firestore immediately
        try {
            await setDoc(doc(db, "feed", request.id), request);
            console.log(`Flagged request ${request.id}`);
            setNotificationSettings({open: true, message: "request flagged"});
            setTimeout(() => setNotificationSettings({open: false, message: ""}), 5000)
        } catch (error) {
            console.error('Error flagging request:', error);
            alert(`Failed to flag request: ${error.message}`);
        }
    }

    const didChangeNSFW = (event) => {
        setNSFW(event.target.checked)
    };

    const didChangeIPSetting = (event) => {
        setContainsIP(event.target.checked)
    };

    const onLoad = (element) => {
        setDimensions(`${util.inspect(imgRef.current.naturalWidth)} x ${util.inspect(imgRef.current.naturalHeight)}`);
    }

    return (
        <Fade in timeout={300}>
            <Card
                sx={{
                    borderRadius: 2,
                    borderLeft: 6,
                    borderLeftColor: 'primary.main',
                    transition: 'box-shadow 0.2s',
                    '&:hover': {
                        boxShadow: 4
                    }
                }}
            >
                <Grid container>
                    {/* Image Section */}
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                height: { xs: 400, md: '100%' },
                                minHeight: { md: 600 },
                                bgcolor: 'grey.100',
                                overflow: 'hidden'
                            }}
                        >
                            <img
                                ref={imgRef}
                                src={props.request.imageUrl}
                                onLoad={onLoad}
                                alt="Generated"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </Box>
                    </Grid>

                    {/* Content Section */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            {/* Header */}
                            <Box sx={{ p: 3, pb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Chip
                                        label="PENDING"
                                        color="warning"
                                        size="small"
                                        sx={{ fontWeight: 600 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {formatDate(fromFirebase(props.request.submittedAt))}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    <strong>ID:</strong> {props.request.id}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>User:</strong> {username || 'Loading...'}
                                </Typography>
                            </Box>

                            <Divider />

                            {/* Metadata */}
                            <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                                <Stack spacing={2.5}>
                                    {/* Image Info */}
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                                            Image Details
                                        </Typography>
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}>
                                                <Typography variant="body2">
                                                    <strong>Type:</strong> {props.request.workflow.workflowType || 'txt2img'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2">
                                                    <strong>Dimensions:</strong> {dimensions || 'Loading...'}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    {/* Generation Parameters */}
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                                            Generation Parameters
                                        </Typography>
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}>
                                                <Typography variant="body2">
                                                    <strong>Steps:</strong> {props.request.workflow.numInferenceSteps}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2">
                                                    <strong>CFG Scale:</strong> {props.request.workflow.cfgScale}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2">
                                                    <strong>Sampler:</strong> {props.request.workflow.sampler}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2">
                                                    <strong>Seed:</strong> {props.request.workflow.seed}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    {/* Models */}
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                                            Models & Resources
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                                            <strong>Checkpoint:</strong> {props.request.workflow.resources?.find(r => r.type === 'checkpoint')?.modelVersionName || '[unknown]'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>LoRAs:</strong> {props.request.workflow.resources?.filter(r => r.type === 'lora').map(r => r.modelVersionName).join(', ') || '[none]'}
                                        </Typography>
                                    </Box>

                                    {/* Prompts */}
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                                            Positive Prompt
                                        </Typography>
                                        <Paper
                                            elevation={0}
                                            sx={(theme) => ({
                                                p: 1.5,
                                                bgcolor: theme.palette.mode === 'dark' ? '#0d1b2a' : 'grey.50',
                                                borderRadius: 1,
                                                maxHeight: 120,
                                                overflow: 'auto'
                                            })}
                                        >
                                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                                {props.request.workflow.positivePrompt}
                                            </Typography>
                                        </Paper>
                                    </Box>

                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                                            Negative Prompt
                                        </Typography>
                                        <Paper
                                            elevation={0}
                                            sx={(theme) => ({
                                                p: 1.5,
                                                bgcolor: theme.palette.mode === 'dark' ? '#0d1b2a' : 'grey.50',
                                                borderRadius: 1,
                                                maxHeight: 120,
                                                overflow: 'auto'
                                            })}
                                        >
                                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                                {props.request.workflow.negativePrompt}
                                            </Typography>
                                        </Paper>
                                    </Box>

                                    {/* Moderation Flags */}
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                                            Moderation Flags
                                        </Typography>
                                        <Stack spacing={1} sx={{ ml: 0 }}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={isNSFW}
                                                        onChange={didChangeNSFW}
                                                        color="warning"
                                                        size="small"
                                                    />
                                                }
                                                label={<Typography variant="body2">NSFW Content</Typography>}
                                                sx={{ ml: 0 }}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={containsIP}
                                                        onChange={didChangeIPSetting}
                                                        color="error"
                                                        size="small"
                                                    />
                                                }
                                                label={<Typography variant="body2">Contains IP</Typography>}
                                                sx={{ ml: 0 }}
                                            />
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Box>

                            <Divider />

                            {/* Actions */}
                            <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Tooltip title="Approve">
                                    <IconButton
                                        onClick={() => approveRequest(props.request)}
                                        color="success"
                                        size="large"
                                        sx={{
                                            '&:hover': {
                                                bgcolor: 'success.light',
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        <ThumbUp />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject">
                                    <IconButton
                                        onClick={() => rejectRequest(props.request)}
                                        color="error"
                                        size="large"
                                        sx={{
                                            '&:hover': {
                                                bgcolor: 'error.light',
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        <ThumbDown />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Flag for review">
                                    <IconButton
                                        onClick={() => flagRequest(props.request)}
                                        color="warning"
                                        size="large"
                                        sx={{
                                            '&:hover': {
                                                bgcolor: 'warning.light',
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        <Flag />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Card>
        </Fade>
    )
}